import {Button} from '@/components/ui/button/Button'
import {PencilLineIcon, Star01Icon} from '@/components/ui/icon/Icon'
import {useURLParsedParams} from '@/hooks/useURLParsedParams'
import {useTranslation} from '@/translations/i18n'
import {UserAccessProfileValidationSchema, UserExtended} from '@/features/users/types'
import {useGetUser} from '@/features/users/queries/useGetUser'
import {ObjectKeys, raise} from '@/utilities/helpers'
import {FormProvider, useForm, useWatch} from 'react-hook-form'
import {UserAccessProfileForm} from '@/features/users/components/user-access-profile-form/UserAccessProfileForm'
import {zodResolver} from '@hookform/resolvers/zod'
import {useState} from 'react'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {useUpdateUser} from '@/features/users/queries/useUpdateUser'
import {toast} from '@/utilities/toast/toast'
import {remapUserToPayload} from '@/features/users/utils'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {UpdateUserAccessProfileConfirmationModal} from '@/features/users/components/update-user-access-profile-confirmation-modal/UpdateUserAccessProfileConfirmationModal'
import {UserDetailsUrlParams} from '@/types'
import {usePermissions} from '@/features/auth/store/store'
import {authPermissionsToPermissionKeys} from '@/features/auth/utils'
import {ScrollArea} from '@/components/ui/scroll-area/ScrollArea'
import {Scrollbar} from '@/components/ui/scrollbar/Scrollbar'

export const UserDetailsAccessProfile = () => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const urlParams = useURLParsedParams(UserDetailsUrlParams)
    const userQuery = useGetUser({
        userId: urlParams.success ? urlParams.data.id : undefined,
        responseType: 'extended',
        options: {enabled: !!urlParams.success}
    })
    const [isEditableAccessProfile, setIsEditableAccessProfile] = useState(false)
    const user = userQuery.data ?? raise('user is nullish')
    if (user.role || isEditableAccessProfile) {
        return (
            <UserDetailAccessProfileRole
                user={user}
                isEditableAccessProfile={isEditableAccessProfile}
                setIsEditableAccessProfile={setIsEditableAccessProfile}
            />
        )
    }

    return (
        <DetailsFieldsSection
            title={t('users:access_profile')}
            subtitle={t('users:access_profile_not_available', {
                fullName: user.fullName
            })}
            headingEndSlot={
                permissions?.users?.write && (
                    <Button variant="ghost" shape="text" onClick={() => setIsEditableAccessProfile(true)}>
                        <Star01Icon />
                        {t('users:enable_access_profile')}
                    </Button>
                )
            }
        >
            <></>
        </DetailsFieldsSection>
    )
}

export const UserDetailAccessProfileRole = ({
    user,
    isEditableAccessProfile,
    setIsEditableAccessProfile
}: {
    user: UserExtended
    isEditableAccessProfile: boolean
    setIsEditableAccessProfile: (value: boolean) => void
}) => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const [isConfirmationModalVisible, setIsConfirmationModalVisible] = useState(false)

    // edit form methods
    const form = useForm<UserAccessProfileValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(UserAccessProfileValidationSchema),
        defaultValues: {
            permissions: user.permissions ? authPermissionsToPermissionKeys(user.permissions) : [],
            permissionsOverwritten: user.permissionsOverwritten,
            role: user.role
        }
    })

    // form obeserver
    const role = useWatch({control: form.control, name: 'role'})

    // mutation
    const {mutate: updateUserMutation, isPending: isPendingUpdate} = useUpdateUser({
        urlParams: {userId: user.id},
        params: {responseType: 'extended'},
        options: {
            onSuccess: user => {
                setIsConfirmationModalVisible(false)
                setIsEditableAccessProfile(false)
                toast.success(t('commons:update_success'))
                form.reset({
                    permissions: user.permissions ? authPermissionsToPermissionKeys(user.permissions) : [],
                    permissionsOverwritten: user.permissionsOverwritten,
                    role: user.role
                })

                void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.USERS, user.id]})
            }
        }
    })

    // actions
    const handleSubmit = form.handleSubmit(() => setIsConfirmationModalVisible(true))
    const handleConfirmSubmit = form.handleSubmit(formValues => {
        const parsedValues = remapUserToPayload(formValues)
        return updateUserMutation(parsedValues)
    })

    return (
        <ScrollArea scrollbar={<Scrollbar />}>
            <DetailsFieldsSection
                title={t('users:access_to_star')}
                subtitle={user.role?.name}
                headingEndSlot={
                    permissions?.users?.write && (
                        <>
                            {isEditableAccessProfile ? (
                                <Flexbox>
                                    <Button
                                        disabled={isPendingUpdate}
                                        variant="ghost"
                                        onClick={() => {
                                            form.reset()
                                            setIsEditableAccessProfile(false)
                                        }}
                                    >
                                        {t('commons:cancel')}
                                    </Button>
                                    <Button
                                        disabled={isPendingUpdate || !ObjectKeys(form.formState.dirtyFields).length}
                                        variant="primaryNeutral"
                                        onClick={handleSubmit}
                                    >
                                        {isPendingUpdate && <Spinner />}
                                        {t('commons:confirm')}
                                    </Button>
                                </Flexbox>
                            ) : (
                                <Button variant="ghost" shape="text" onClick={() => setIsEditableAccessProfile(true)}>
                                    <PencilLineIcon />
                                    {t('commons:edit')}
                                </Button>
                            )}
                        </>
                    )
                }
            >
                <FormProvider {...form}>
                    <UserAccessProfileForm hideRoleGridHeader={!isEditableAccessProfile} />
                    {isConfirmationModalVisible && (
                        <UpdateUserAccessProfileConfirmationModal
                            onClose={() => setIsConfirmationModalVisible(false)}
                            onConfirm={handleConfirmSubmit}
                            isConfirmPending={isPendingUpdate}
                            user={user}
                            roleName={role?.name}
                        />
                    )}
                </FormProvider>
            </DetailsFieldsSection>
        </ScrollArea>
    )
}
